import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
} from "reactstrap";
import { FileTypeIcon } from "components/common/FileTypeIcon";
import { useFormik } from "formik";

import { getEnquirySiteVisitFormSchema } from "./schema";
import {
  FileDropzone,
  DEFAULT_IMAGES_AND_PDF_ACCEPT,
  DEFAULT_MAX_FILE_BYTES,
} from "components/common/FileDropzone";
import { formatFileSize } from "utils/fileUtils";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import BreadCrumb from "components/common/BreadCrumb";
import {
  EnquirySiteVisitPriority,
  EnquirySiteVisitPriorityMap,
} from "features/EnquirySiteVisits/constants/enquiry-site-visit.constants";
import type { SiteVisitChecklistItem } from "features/EnquirySiteVisits/types/enquiry-site-visit.types";
import { toast } from "react-toastify";
import {
  createEnquirySiteVisit,
  getEnquirySiteVisit,
  updateEnquirySiteVisit,
} from "features/EnquirySiteVisits/services/EnquirySiteVisitService";
import type { EnquirySiteVisit } from "features/EnquirySiteVisits/types/enquiry-site-visit.types";
import { BackButton } from "components/common/Buttons";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import { getEnquiryOptions } from "features/Enquiries/services/EnquiryService";
import {
  buildEnquirySelectDefaultOption,
  enrichEnquirySelectOptionData,
  EnquiryOption,
  EnquirySingleValue,
} from "features/Enquiries/components/EnquirySelectComponents";
import { getDocumentTitle } from "utils/helpers";
import {
  hasAnyEnquirySiteVisitEditPermission,
  isEnquirySiteVisitFieldEditable,
} from "features/EnquirySiteVisits/utils/enquirySiteVisitEditFieldPolicy";
import AppFlatpickr from "components/common/AppFlatpickr";
import moment from "moment";

const API_BASE = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "");

function resolveAttachmentSrc(pathOrUrl: string): string {
  const t = pathOrUrl.trim();
  if (!t) return "";
  if (/^https?:\/\//i.test(t)) return t;
  const path = t.replace(/^\//, "");
  if (!API_BASE) return `/${path}`;
  return `${API_BASE}/${path}`;
}

function attachmentLabel(url: string): string {
  try {
    if (/^https?:\/\//i.test(url)) {
      const u = new URL(url);
      const last = u.pathname.split("/").filter(Boolean).pop();
      return last ? decodeURIComponent(last) : url;
    }
  } catch {
    /* ignore */
  }
  if (url.includes("uploads/")) {
    return decodeURIComponent(url.split("uploads/").pop() || url);
  }
  const seg = url.split("/").pop();
  return decodeURIComponent(seg || url);
}

type FileWithPreview = File & { preview?: string };

function attachmentsToPathList(
  raw: EnquirySiteVisit["attachments"] | undefined,
): string[] {
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch {
      return raw.trim() ? [raw] : [];
    }
  }
  return [];
}

const EnquirySiteVisitForm = ({
  isEditMode = false,
}: {
  isEditMode?: boolean;
}) => {
  const [selectedFiles, setselectedFiles] = useState<FileWithPreview[]>([]);
  const [existingFiles, setExistingFiles] = useState<string[]>([]);
  const [deletedFiles, setDeletedFiles] = useState<string[]>([]);
  const [record, setRecord] = useState<EnquirySiteVisit>();
  const selectedFilesRef = useRef<FileWithPreview[]>([]);
  const newChecklistItemRef = useRef<HTMLInputElement | null>(null);
  const [expandedFindings, setExpandedFindings] = useState<Set<number>>(
    new Set(),
  );

  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const defaultEnquiry = (location.state as { defaultEnquiry?: any } | null)?.defaultEnquiry ?? null;

  document.title = getDocumentTitle(
    !isEditMode ? "Create Site Visit" : "Edit Site Visit",
  );

  selectedFilesRef.current = selectedFiles;

  useEffect(() => {
    return () => {
      selectedFilesRef.current.forEach((f) => {
        if (f.preview?.startsWith("blob:")) URL.revokeObjectURL(f.preview);
      });
    };
  }, []);

  const canEdit = useMemo(() => {
    if (!isEditMode) {
      return () => true;
    }
    if (!record) {
      return () => false;
    }
    const st = record.status;
    return (field: string) =>
      isEnquirySiteVisitFieldEditable(field, String(st), true);
  }, [isEditMode, record]);

  const canManageChecklistStructure = canEdit("checklist");
  const canFillChecklistFindings = canEdit("checklist");

  const addChecklistItem = () => {
    const current: SiteVisitChecklistItem[] = validation.values.checklist ?? [];
    const nextOrder =
      current.length > 0
        ? Math.max(...current.map((i: SiteVisitChecklistItem) => i.sortOrder)) +
          1
        : 0;
    void validation.setFieldValue("checklist", [
      ...current,
      { label: "", finding: null, isCompleted: false, sortOrder: nextOrder },
    ]);
    setTimeout(() => newChecklistItemRef.current?.focus(), 30);
  };

  const removeChecklistItem = (idx: number) => {
    const current: SiteVisitChecklistItem[] = validation.values.checklist ?? [];
    void validation.setFieldValue(
      "checklist",
      current.filter((_: SiteVisitChecklistItem, i: number) => i !== idx),
    );
    setExpandedFindings((prev) => {
      const next = new Set<number>();
      prev.forEach((n) => {
        if (n < idx) next.add(n);
        else if (n > idx) next.add(n - 1);
      });
      return next;
    });
  };

  const updateChecklistItem = (
    idx: number,
    updates: Partial<SiteVisitChecklistItem>,
  ) => {
    const current: SiteVisitChecklistItem[] = validation.values.checklist ?? [];
    void validation.setFieldValue(
      "checklist",
      current.map((item: SiteVisitChecklistItem, i: number) =>
        i === idx ? { ...item, ...updates } : item,
      ),
    );
  };

  const toggleFinding = (idx: number) => {
    setExpandedFindings((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  useEffect(() => {
    const checklist = record?.checklist;
    if (Array.isArray(checklist)) {
      setExpandedFindings(
        new Set(
          checklist.reduce<number[]>((acc, item, idx) => {
            if (item.finding) acc.push(idx);
            return acc;
          }, []),
        ),
      );
    }
  }, [record?.checklist]);

  const canEditAttachments = canEdit("attachments");

  const enquirySelectComponents = useMemo(
    () => ({
      Option: EnquiryOption,
      SingleValue: (p: any) => (
        <EnquirySingleValue
          {...p}
          data={enrichEnquirySelectOptionData(p.data, record?.enquiry ?? defaultEnquiry ?? null)}
        />
      ),
    }),
    [record?.enquiry, defaultEnquiry],
  );

  const editBlockedEntirely =
    isEditMode &&
    record != null &&
    !hasAnyEnquirySiteVisitEditPermission(record.status);

  // Memoised so the reference only changes when the loaded record changes.
  // Without this, Formik's enableReinitialize useEffect fires on every render
  // (because a plain object literal is always a new reference) and resets
  // all fields whenever any unrelated state update occurs (e.g. file drop).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const formInitialValues = useMemo(
    () => ({
      address: record?.address ?? "",
      city: record?.city ?? "",
      state: record?.state ?? "",
      enquiryId: record?.enquiryId ?? (defaultEnquiry?.id ? String(defaultEnquiry.id) : ""),
      scheduledAt: record?.scheduledAt ? moment(record.scheduledAt).format("YYYY-MM-DD") : "",
      description: record?.description ?? "",
      priority:
        (record?.priority as string | undefined) ??
        EnquirySiteVisitPriority.MEDIUM,
      checklist: Array.isArray(record?.checklist)
        ? [...record.checklist].sort(
            (a: SiteVisitChecklistItem, b: SiteVisitChecklistItem) =>
              a.sortOrder - b.sortOrder,
          )
        : ([] as SiteVisitChecklistItem[]),
      attachments: [] as File[],
      existingFiles: record?.attachments ?? [],
      deletedFiles: [] as string[],
      files: [] as File[],
    }),
    // Intentionally exclude deletedFiles/selectedFiles — they are managed as
    // separate state and read directly on submit. Including them would trigger
    // a full form reset every time a file is added or removed.
    [record], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: formInitialValues,

    validationSchema: getEnquirySiteVisitFormSchema(isEditMode),
    onSubmit: async (values) => {
      if (
        isEditMode &&
        record &&
        !hasAnyEnquirySiteVisitEditPermission(record.status)
      ) {
        return;
      }
      const formData = new FormData();
      formData.append("address", values.address);
      formData.append("city", values.city);
      formData.append("state", values.state);
      formData.append("priority", values.priority);
      if (values.scheduledAt) formData.append("scheduledAt", values.scheduledAt);
      formData.append("enquiryId", values.enquiryId);
      formData.append("description", values.description);
      if (isEditMode) {
        formData.append(
          "checklist",
          JSON.stringify(
            (values.checklist as SiteVisitChecklistItem[]).map((item, idx) => ({
              ...item,
              sortOrder: idx,
              label: item.label.trim(),
              finding: item.finding?.trim() || null,
            })),
          ),
        );
      }
      deletedFiles.forEach((url) => formData.append("removeAttachments", url));
      values.files?.forEach((file: File) => {
        formData.append("files", file);
      });

      try {
        let response;

        if (isEditMode) {
          response = await updateEnquirySiteVisit(Number(id), formData);
        } else {
          response = await createEnquirySiteVisit(formData);
        }

        if (response.success) {
          toast.success(response.message);
          navigate(-1);
        }
      } catch (err) {
        const msg =
          (err as any)?.response?.data?.message ??
          (err as any)?.message ??
          "Something went wrong";
        toast.error(msg);
        validation.setFieldError("files", msg);
        void validation.setFieldTouched("files", true, false);
        console.log(err);
      }
    },
  });

  const appendDroppedFiles = (incoming: File[]) => {
    const allowedMime = new Set(["image/png", "image/jpeg", "application/pdf"]);
    const allowedExt = new Set([".png", ".jpg", ".jpeg", ".pdf"]);
    const valid: File[] = [];
    const skipped: File[] = [];
    for (const file of incoming) {
      const ext = ("." + (file.name.split(".").pop() ?? "")).toLowerCase();
      if (allowedMime.has(file.type) && allowedExt.has(ext)) {
        valid.push(file);
      } else {
        skipped.push(file);
      }
    }
    if (skipped.length > 0) {
      toast.error(
        `${skipped.length} file${skipped.length > 1 ? "s" : ""} skipped — only PNG, JPG, JPEG, or PDF are accepted.`,
      );
    }
    if (valid.length === 0) return;
    const stamped: FileWithPreview[] = valid.map((file) =>
      Object.assign(file as FileWithPreview, {
        preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : undefined,
      }),
    );
    const next = [...selectedFiles, ...stamped];
    setselectedFiles(next);
    void validation.setFieldValue("files", next);
  };

  const removeNewFileAt = (index: number) => {
    const file = selectedFiles[index];
    if (file?.preview?.startsWith("blob:")) URL.revokeObjectURL(file.preview);
    const next = selectedFiles.filter((_, i) => i !== index);
    setselectedFiles(next);
    void validation.setFieldValue("files", next);
  };

  function handleDeleteExistingImage(i: number) {
    const url = existingFiles[i];
    const updated = existingFiles.filter((_, index) => index !== i);
    setExistingFiles(updated);
    void validation.setFieldValue("existingFiles", updated);

    const newDeleted = url ? [...deletedFiles, url] : deletedFiles;
    setDeletedFiles(newDeleted);
    void validation.setFieldValue("deletedFiles", newDeleted);
  }

  useEffect(() => {
    const fetchRecord = async () => {
      try {
        const response = await getEnquirySiteVisit(Number(id));
        const { success, message, data } = response;
        if (success) {
          setRecord(data);
          setExistingFiles(attachmentsToPathList(data.attachments));
        }
        console.log(message);
      } catch (err) {
        console.warn(err);
      }
    };

    if (isEditMode) {
      fetchRecord();
    }
  }, [id, isEditMode]);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={!isEditMode ? "Create Site Visit" : "Edit Site Visit"}
          pageTitle="Enquiry Management"
          items={
            !isEditMode && defaultEnquiry?.id
              ? [
                  {
                    label: defaultEnquiry.code ?? `Enquiry #${defaultEnquiry.id}`,
                    path: `/enquiry-management/enquiries/${defaultEnquiry.id}`,
                  },
                ]
              : [
                  { label: "Site visits", path: "/enquiry-management/site-visits" },
                ]
          }
        />
        <Form
          className="needs-validation"
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
        >
          <Row className="g-3 align-items-start">
            <Col lg={isEditMode && canEdit("checklist") ? 8 : 12}>
              <Card className="shadow-none border">
                <CardHeader className="border-bottom bg-transparent pb-3">
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                    <h5 className="card-title mb-0">
                      {!isEditMode ? "Create site visit" : "Edit site visit"}
                    </h5>
                  </div>
                  <p className="text-muted mb-0 small">
                    {!isEditMode
                      ? "Choose an enquiry and set the location. The inspection checklist can be added once the visit is created."
                      : "Update the site visit details below."}
                  </p>
                </CardHeader>

                <CardBody className="pt-4">
                  {editBlockedEntirely ? (
                    <Alert color="warning" className="mb-4" role="alert">
                      <i className="ri-information-line me-2 align-middle" />
                      This site visit cannot be edited in its current status
                      (completed or cancelled).
                    </Alert>
                  ) : null}

                  {/* Enquiry & details */}
                  <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                    Enquiry &amp; details
                  </div>
                  <Row className="g-3 mb-4">
                    <Col md={12}>
                      <Label className="form-label">Enquiry</Label>
                      <AsyncPaginatedSelect
                        components={enquirySelectComponents}
                        defaultOption={buildEnquirySelectDefaultOption(
                          record?.enquiry ?? defaultEnquiry,
                        )}
                        fetchOptions={getEnquiryOptions}
                        placeholder="Search enquiries…"
                        isDisabled={!!defaultEnquiry || !canEdit("enquiryId")}
                        value={validation.values.enquiryId}
                        onChange={(val: any) =>
                          validation.setFieldValue("enquiryId", val?.value)
                        }
                        onBlur={() =>
                          validation.setFieldTouched("enquiryId", true)
                        }
                        isInvalid={
                          validation.touched.enquiryId &&
                          validation.errors.enquiryId
                        }
                      />
                      {validation.touched.enquiryId &&
                      validation.errors.enquiryId ? (
                        <FormFeedback type="invalid" className="d-block">
                          {validation.errors.enquiryId}
                        </FormFeedback>
                      ) : null}
                    </Col>
                    <Col md={4}>
                      <Label className="form-label">Priority</Label>
                      <Input
                        type="select"
                        name="priority"
                        className="form-select"
                        disabled={!canEdit("priority")}
                        value={validation.values.priority}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={
                          !!(
                            validation.touched.priority &&
                            validation.errors.priority
                          )
                        }
                      >
                        {(
                          Object.values(EnquirySiteVisitPriority) as string[]
                        ).map((val) => (
                          <option key={val} value={val}>
                            {EnquirySiteVisitPriorityMap[val]?.label ?? val}
                          </option>
                        ))}
                      </Input>
                      {validation.touched.priority &&
                      validation.errors.priority ? (
                        <FormFeedback type="invalid">
                          {validation.errors.priority}
                        </FormFeedback>
                      ) : null}
                    </Col>
                    <Col md={4}>
                      <Label className="form-label">Scheduled date</Label>
                      <AppFlatpickr
                        name="scheduledAt"
                        className={`form-control${
                          validation.touched.scheduledAt &&
                          validation.errors.scheduledAt
                            ? " is-invalid"
                            : ""
                        }`}
                        placeholder="Select date"
                        options={{
                          dateFormat: "Y-m-d",
                          altInput: true,
                          altFormat: "d M, Y",
                        }}
                        onChange={(date: Date[]) => {
                          void validation.setFieldValue(
                            "scheduledAt",
                            date[0] ? moment(date[0]).format("YYYY-MM-DD") : "",
                          );
                          void validation.setFieldTouched("scheduledAt", true);
                        }}
                        value={validation.values.scheduledAt || ""}
                        disabled={!canEdit("scheduledAt")}
                      />
                      {validation.touched.scheduledAt &&
                      validation.errors.scheduledAt ? (
                        <FormFeedback type="invalid" className="d-block">
                          {validation.errors.scheduledAt}
                        </FormFeedback>
                      ) : null}
                    </Col>
                  </Row>

                  <hr className="my-4 text-muted opacity-25" />

                  {/* Location */}
                  <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                    Location
                  </div>
                  <Row className="g-3 mb-4">
                    <Col md={12}>
                      <Label className="form-label">Address</Label>
                      <Input
                        name="address"
                        className="form-control"
                        placeholder="Enter address"
                        type="textarea"
                        rows={3}
                        disabled={!canEdit("address")}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.address || ""}
                        invalid={
                          !!(
                            validation.touched.address &&
                            validation.errors.address
                          )
                        }
                      />
                      {validation.touched.address &&
                      validation.errors.address ? (
                        <FormFeedback type="invalid">
                          {validation.errors.address}
                        </FormFeedback>
                      ) : null}
                    </Col>
                    <Col md={6}>
                      <Label className="form-label">City</Label>
                      <Input
                        name="city"
                        className="form-control"
                        placeholder="Enter city"
                        disabled={!canEdit("city")}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.city || ""}
                        invalid={
                          !!(validation.touched.city && validation.errors.city)
                        }
                      />
                      {validation.touched.city && validation.errors.city ? (
                        <FormFeedback type="invalid">
                          {validation.errors.city}
                        </FormFeedback>
                      ) : null}
                    </Col>
                    <Col md={6}>
                      <Label className="form-label">State</Label>
                      <Input
                        name="state"
                        className="form-control"
                        placeholder="Enter state"
                        type="text"
                        disabled={!canEdit("state")}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.state || ""}
                        invalid={
                          !!(
                            validation.touched.state && validation.errors.state
                          )
                        }
                      />
                      {validation.touched.state && validation.errors.state ? (
                        <FormFeedback type="invalid">
                          {validation.errors.state}
                        </FormFeedback>
                      ) : null}
                    </Col>
                  </Row>

                  <hr className="my-4 text-muted opacity-25" />

                  {/* Notes */}
                  <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                    Notes
                  </div>
                  <Row className="mb-4">
                    <Col md={12}>
                      <Input
                        name="description"
                        className="form-control"
                        type="textarea"
                        rows={5}
                        placeholder="Add visit notes"
                        disabled={!canEdit("description")}
                        value={validation.values.description || ""}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={
                          !!(
                            validation.touched.description &&
                            validation.errors.description
                          )
                        }
                      />
                      {validation.touched.description &&
                      validation.errors.description ? (
                        <div className="invalid-feedback d-block">
                          {validation.errors.description}
                        </div>
                      ) : null}
                    </Col>
                  </Row>

                  <hr className="my-4 text-muted opacity-25" />

                  {/* Photos & documents */}
                  <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                    Photos &amp; documents
                  </div>

                  {existingFiles.length > 0 ? (
                    <div className="mb-3">
                      <Label className="form-label fw-medium">Existing files</Label>
                      <div className="vstack gap-2">
                        {existingFiles.map((f, i) => {
                          const src = resolveAttachmentSrc(f);
                          const label = attachmentLabel(f);
                          return (
                            <div
                              key={`existing-${i}-${label}`}
                              className="d-flex align-items-center gap-2 border rounded p-2 bg-light bg-opacity-50"
                            >
                              <FileTypeIcon
                                filename={f}
                                imageSrc={resolveAttachmentSrc(f)}
                                className="rounded flex-shrink-0"
                                style={{ width: 40, height: 40 }}
                              />
                              <div className="flex-grow-1 min-w-0">
                                <div className="text-truncate small fw-medium">{label}</div>
                              </div>
                              <a
                                href={src}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-soft-secondary btn-sm px-2 py-1 flex-shrink-0"
                              >
                                <i className="ri-eye-line" />
                              </a>
                              <Button
                                type="button"
                                color="danger"
                                outline
                                size="sm"
                                className="px-2 py-1 flex-shrink-0"
                                title="Remove attachment"
                                disabled={!canEditAttachments}
                                onClick={() => handleDeleteExistingImage(i)}
                              >
                                <i className="ri-delete-bin-line" />
                              </Button>
                            </div>
                          );
                        })}
                        {deletedFiles.length > 0 ? (
                          <p className="text-muted small mb-0">
                            <i className="ri-information-line me-1" />
                            {deletedFiles.length} file{deletedFiles.length > 1 ? "s" : ""} will be removed on save.
                          </p>
                        ) : null}
                      </div>
                    </div>
                  ) : null}

                  <div>
                    <Label className="form-label fw-medium">
                      Upload files
                      {selectedFiles.length > 0 ? (
                        <span className="text-muted fw-normal ms-1">
                          ({selectedFiles.length} {selectedFiles.length === 1 ? "file" : "files"})
                        </span>
                      ) : null}
                    </Label>
                    <FileDropzone
                      accept={DEFAULT_IMAGES_AND_PDF_ACCEPT}
                      maxSize={DEFAULT_MAX_FILE_BYTES}
                      invalidTypeMessage="Only PNG, JPG, JPEG, or PDF files are accepted"
                      disabled={validation.isSubmitting || !canEditAttachments}
                      invalid={Boolean(validation.touched.files && validation.errors.files)}
                      errorText={validation.errors.files}
                      compact
                      description={`PNG, JPG, PDF · Max ${formatFileSize(DEFAULT_MAX_FILE_BYTES)} per file`}
                      notifyOnReject
                      onDropAccepted={(acceptedFiles) => {
                        appendDroppedFiles(acceptedFiles);
                      }}
                    />
                    {selectedFiles.length > 0 ? (
                      <div className="mt-3 vstack gap-2">
                        {selectedFiles.map((f, i) => (
                          <div
                            key={`${f.name}-${f.size}-${i}`}
                            className="d-flex align-items-center gap-2 border rounded p-2"
                          >
                            <FileTypeIcon
                              filename={f.name}
                              imageSrc={f.preview}
                              className="rounded flex-shrink-0"
                              style={{ width: 40, height: 40 }}
                            />
                            <div className="flex-grow-1 min-w-0">
                              <div className="text-truncate small fw-medium">{f.name}</div>
                              <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                                {formatFileSize(f.size)}
                              </div>
                            </div>
                            <Button
                              type="button"
                              close
                              disabled={!canEditAttachments}
                              onClick={() => removeNewFileAt(i)}
                            />
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </CardBody>

                <CardFooter className="bg-light bg-opacity-50 border-top d-flex flex-wrap justify-content-between align-items-center gap-2 py-3">
                  <BackButton />
                  <Button
                    type="submit"
                    color="primary"
                    disabled={validation.isSubmitting || editBlockedEntirely}
                  >
                    {validation.isSubmitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        {isEditMode ? "Updating…" : "Submitting…"}
                      </>
                    ) : isEditMode ? (
                      "Save changes"
                    ) : (
                      "Create site visit"
                    )}
                  </Button>
                </CardFooter>
              </Card>
            </Col>

            {isEditMode && canEdit("checklist") ? (
              <Col lg={4}>
                <Card className="shadow-none border">
                  <CardHeader className="border-bottom bg-transparent pb-3">
                    <div className="d-flex align-items-start justify-content-between gap-2">
                      <div>
                        <h6 className="card-title mb-1">
                          Inspection checklist
                        </h6>
                        <p className="text-muted small mb-0">
                          Add items that need to be inspected during the visit.
                        </p>
                      </div>
                      {canManageChecklistStructure ? (
                        <button
                          type="button"
                          className="btn btn-light btn-sm border flex-shrink-0"
                          onClick={addChecklistItem}
                        >
                          <i className="ri-add-line align-bottom me-1" />
                          Add item
                        </button>
                      ) : null}
                    </div>
                  </CardHeader>
                  <CardBody>
                    {(validation.values.checklist as SiteVisitChecklistItem[])
                      .length === 0 ? (
                      <div className="text-center py-4">
                        <i className="ri-checkbox-multiple-line fs-2 text-muted opacity-50" />
                        <p className="text-muted small mt-2 mb-0">
                          No items yet.
                        </p>
                        {canManageChecklistStructure ? (
                          <button
                            type="button"
                            className="btn btn-primary btn-sm mt-3"
                            onClick={addChecklistItem}
                          >
                            <i className="ri-add-line me-1" />
                            Add first item
                          </button>
                        ) : null}
                      </div>
                    ) : (
                      <div className="d-flex flex-column gap-2">
                        {(
                          validation.values
                            .checklist as SiteVisitChecklistItem[]
                        ).map((item, idx, arr) => (
                          <div
                            key={idx}
                            className={`p-3 rounded border${item.isCompleted ? " border-success bg-success bg-opacity-10" : ""}`}
                          >
                            <div className="d-flex gap-3 align-items-start">
                              <div
                                className="d-flex flex-column align-items-center gap-1 pt-1 flex-shrink-0"
                                style={{ width: "24px" }}
                              >
                                <span
                                  className="text-muted fw-semibold"
                                  style={{ fontSize: "10px", lineHeight: 1 }}
                                >
                                  {idx + 1}
                                </span>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id={`checklist-form-item-${idx}`}
                                  checked={item.isCompleted}
                                  disabled={!canFillChecklistFindings}
                                  onChange={(e) =>
                                    updateChecklistItem(idx, {
                                      isCompleted: e.target.checked,
                                    })
                                  }
                                />
                              </div>
                              <div className="flex-grow-1 min-w-0">
                                {canManageChecklistStructure ? (
                                  <Input
                                    type="text"
                                    bsSize="sm"
                                    placeholder="What to inspect…"
                                    value={item.label}
                                    innerRef={
                                      idx === arr.length - 1
                                        ? newChecklistItemRef
                                        : undefined
                                    }
                                    className={`fw-medium border-0 shadow-none bg-transparent px-0 py-0${item.isCompleted ? " text-decoration-line-through text-muted" : ""}`}
                                    onChange={(e) =>
                                      updateChecklistItem(idx, {
                                        label: e.target.value,
                                      })
                                    }
                                  />
                                ) : (
                                  <p
                                    className={`fw-medium mb-0${item.isCompleted ? " text-decoration-line-through text-muted" : ""}`}
                                  >
                                    {item.label || (
                                      <span className="text-muted fst-italic">
                                        Untitled item
                                      </span>
                                    )}
                                  </p>
                                )}
                                {canFillChecklistFindings ? (
                                  <div className="mt-2">
                                    {expandedFindings.has(idx) ? (
                                      <>
                                        <Input
                                          type="textarea"
                                          bsSize="sm"
                                          rows={2}
                                          placeholder="Record finding…"
                                          value={item.finding ?? ""}
                                          onChange={(e) =>
                                            updateChecklistItem(idx, {
                                              finding: e.target.value || null,
                                            })
                                          }
                                        />
                                        {!item.finding ? (
                                          <button
                                            type="button"
                                            className="btn btn-link btn-sm p-0 text-muted mt-1"
                                            style={{ fontSize: "12px" }}
                                            onClick={() => toggleFinding(idx)}
                                          >
                                            Cancel
                                          </button>
                                        ) : null}
                                      </>
                                    ) : item.finding ? (
                                      <p
                                        className="text-muted small mb-0 fst-italic"
                                        role="button"
                                        onClick={() => toggleFinding(idx)}
                                        title="Click to edit"
                                      >
                                        {item.finding}
                                      </p>
                                    ) : (
                                      <button
                                        type="button"
                                        className="btn btn-link btn-sm p-0 text-muted"
                                        style={{ fontSize: "12px" }}
                                        onClick={() => toggleFinding(idx)}
                                      >
                                        <i className="ri-edit-line me-1" />
                                        Add finding
                                      </button>
                                    )}
                                  </div>
                                ) : item.finding ? (
                                  <p className="text-muted small mb-0 mt-1 fst-italic">
                                    {item.finding}
                                  </p>
                                ) : null}
                              </div>
                              {canManageChecklistStructure ? (
                                <button
                                  type="button"
                                  className="btn btn-link btn-sm text-danger p-0 flex-shrink-0 align-self-start lh-1"
                                  title="Remove item"
                                  onClick={() => removeChecklistItem(idx)}
                                >
                                  <i className="ri-delete-bin-line fs-16" />
                                </button>
                              ) : null}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            ) : null}
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default EnquirySiteVisitForm;
